import React from "react";
import { Check, FileText, ExternalLink, Download, Gavel } from "lucide-react";
import { PageHero } from "@/components/Shared";
import { rulesMeta as M, rulesSections } from "@/pages/rulesContent";

function RuleList({ list }) {
  return (
    <ul className="mt-4 space-y-3">
      {list.map((li) => (
        <li key={li} className="flex gap-3 text-slate-300 leading-relaxed"><Check className="w-4 h-4 text-gold flex-shrink-0 mt-1.5" /><span>{li}</span></li>
      ))}
    </ul>
  );
}

function DocumentCard() {
  const btn = "inline-flex items-center justify-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors";
  return (
    <div className="bg-navy-paper border border-gold/40 p-6 md:p-8 drop-shadow-[0_0_25px_rgba(212,175,55,0.08)]" data-testid="tda-document-card">
      <div className="flex items-start gap-4">
        <FileText className="w-10 h-10 text-gold flex-shrink-0" />
        <div className="flex-1">
          <div className="text-gold text-xs uppercase tracking-[0.3em]">Full Rulebook</div>
          <h2 className="font-display text-2xl font-bold text-white mt-1">2026 Poker TDA Rules — Longform</h2>
          <p className="text-slate-400 text-sm mt-2">Version 1.0, including Recommended Procedures and Illustration Addendum. Legends Poker amendments are shown in red.</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <a href={M.docPdf} target="_blank" rel="noopener noreferrer" className={`${btn} bg-gold text-navy hover:bg-gold-light`} data-testid="tda-view-btn"><ExternalLink className="w-4 h-4" /> View in browser</a>
            <a href={M.docPdf} download className={`${btn} border-2 border-gold text-gold hover:bg-gold hover:text-navy`} data-testid="tda-download-pdf-btn"><Download className="w-4 h-4" /> Download PDF</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Toc() {
  return (
    <nav className="sticky top-28 bg-navy-paper border border-slate-800 p-5" data-testid="rules-toc">
      <div className="text-gold text-xs uppercase tracking-[0.3em] mb-4">Summary</div>
      <ol className="space-y-2 text-sm">
        {rulesSections.map((s, i) => (
          <li key={s.id}><a href={`#${s.id}`} className="flex gap-2 text-slate-400 hover:text-gold transition-colors" data-testid={`rules-toc-${s.id}`}><span className="text-gold/70 w-5 text-right">{i + 1}.</span>{s.title}</a></li>
        ))}
      </ol>
    </nav>
  );
}

export default function RulesPage() {
  return (
    <div data-testid="info-page-rules">
      <PageHero title={M.title} subtitle={M.subtitle} testId="info-hero-rules" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid lg:grid-cols-[260px_1fr] gap-12">
        <aside className="hidden lg:block"><Toc /></aside>
        <div className="max-w-3xl space-y-12">
          <DocumentCard />
          <div className="space-y-3 text-sm">
            <p className="text-slate-500 italic" data-testid="rules-permission">
              TDA rules used by permission of the Poker TDA, Copyright 2026,{" "}
              <a href="http://www.pokertda.com" target="_blank" rel="noopener noreferrer" className="text-gold underline hover:text-gold-light" data-testid="rules-tda-link">http://www.pokertda.com</a>, All rights reserved.
            </p>
            <p className="border-l-2 border-gold bg-gold/5 px-5 py-4 text-slate-200 leading-relaxed text-base" data-testid="rules-disclaimer">{M.disclaimer}</p>
          </div>
          <div className="flex items-center gap-3 border-b border-gold/20 pb-3">
            <Gavel className="w-6 h-6 text-gold" />
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Legends Poker — Summary of TDA Rules with Amendments</h2>
          </div>
          {rulesSections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`rules-section-${s.id}`}>
              <div className="flex items-baseline gap-4">
                <span className="font-display text-3xl font-bold text-gold/60">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-2xl font-bold text-gold">{s.title}</h3>
              </div>
              {s.list && <RuleList list={s.list} />}
              {s.groups && (
                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  {s.groups.map((g) => (
                    <div key={g.h} className="bg-navy-paper border border-slate-800 p-5">
                      <div className="text-gold text-xs font-bold uppercase tracking-widest">{g.h}</div>
                      <RuleList list={g.list} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
